/**
 * useWorkbenchState Hook
 * Main state management for the Behavioral Workbench
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as codeService from '../services/behavioralCodeService';
import { FAANA_DIMENSIONS } from '../constants/workbenchConfig';

/**
 * Main state hook for the Behavioral Workbench
 * @param {string} entityId - Current entity ID
 */
// eslint-disable-next-line max-lines-per-function
export function useWorkbenchState(entityId) {
  // ========================================
  // Code Registry State
  // ========================================
  const [codes, setCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [codesError, setCodesError] = useState(null);

  // ========================================
  // Document State
  // ========================================
  const [documents, setDocuments] = useState([]);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [documentsError, setDocumentsError] = useState(null);

  // ========================================
  // Chunk State
  // ========================================
  const [chunks, setChunks] = useState([]);
  const [selectedChunkIndex, setSelectedChunkIndex] = useState(0);
  const [chunksLoading, setChunksLoading] = useState(false);

  // ========================================
  // UI State
  // ========================================
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(
    FAANA_DIMENSIONS.reduce((acc, dim) => ({ ...acc, [dim]: true }), {})
  );
  const [learningMode, setLearningMode] = useState(false);

  // ========================================
  // AI Suggestion State
  // ========================================
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiHasFetched, setAiHasFetched] = useState(false);
  const [processingAiAction, setProcessingAiAction] = useState(null); // code name being processed
  // Cache: chunkId -> { suggestions, hasFetched } — avoids re-fetching on back-navigation
  const aiCacheRef = useRef(new Map());
  const [aiAutoFetch, setAiAutoFetch] = useState(() => {
    const stored = localStorage.getItem('behavioralWorkbench_aiAutoFetch');
    return stored !== null ? stored === 'true' : true;
  });

  // ========================================
  // Load Behavioral Codes
  // ========================================
  useEffect(() => {
    const loadCodes = async () => {
      try {
        setCodesLoading(true);
        setCodesError(null);
        const loadedCodes = await codeService.listCodes();
        setCodes(loadedCodes);
      } catch (error) {
        console.error('Failed to load codes:', error);
        setCodesError(error.message);
      } finally {
        setCodesLoading(false);
      }
    };

    loadCodes();
  }, []);

  // ========================================
  // Load Documents for Entity
  // ========================================
  useEffect(() => {
    if (!entityId) {
      setDocuments([]);
      setDocumentsLoading(false);
      return;
    }

    const loadDocuments = async () => {
      try {
        setDocumentsLoading(true);
        setDocumentsError(null);
        const loadedDocs = await codeService.getEntityDocuments(entityId);
        setDocuments(loadedDocs);
        setCurrentDocIndex(0);
      } catch (error) {
        console.error('Failed to load documents:', error);
        setDocumentsError(error.message);
      } finally {
        setDocumentsLoading(false);
      }
    };

    loadDocuments();
  }, [entityId]);

  // ========================================
  // Load Chunks for Current Document
  // ========================================
  const currentDocument = documents[currentDocIndex] || null;

  useEffect(() => {
    if (!entityId || !currentDocument?.sourceId) {
      setChunks([]);
      return;
    }

    const loadChunks = async () => {
      try {
        setChunksLoading(true);
        // Clear AI cache when loading a new document's chunks
        aiCacheRef.current.clear();
        const loadedChunks = await codeService.getDocumentChunks(
          entityId,
          currentDocument.sourceId
        );
        setChunks(loadedChunks);
        setSelectedChunkIndex(0);
      } catch (error) {
        console.error('Failed to load chunks:', error);
        setChunks([]);
      } finally {
        setChunksLoading(false);
      }
    };

    loadChunks();
  }, [entityId, currentDocument?.sourceId]);

  // ========================================
  // Derived State
  // ========================================
  const currentChunk = chunks[selectedChunkIndex] || null;

  // Group codes by dimension
  const codesByDimension = useMemo(() => {
    const grouped = FAANA_DIMENSIONS.reduce((acc, dim) => {
      acc[dim] = codes.filter((code) => code.primaryDimension === dim);
      return acc;
    }, {});
    return grouped;
  }, [codes]);

  // Filter codes by search term
  const filteredCodes = useMemo(() => {
    if (!searchTerm.trim()) return codes;

    const term = searchTerm.toLowerCase();
    return codes.filter(
      (code) =>
        code.code.toLowerCase().includes(term) ||
        code.displayName.toLowerCase().includes(term) ||
        code.description.toLowerCase().includes(term)
    );
  }, [codes, searchTerm]);

  // Filtered codes by dimension
  const filteredCodesByDimension = useMemo(() => {
    const grouped = FAANA_DIMENSIONS.reduce((acc, dim) => {
      acc[dim] = filteredCodes.filter((code) => code.primaryDimension === dim);
      return acc;
    }, {});
    return grouped;
  }, [filteredCodes]);

  // Document progress (coded chunks / total chunks)
  const documentProgress = useMemo(() => {
    if (chunks.length === 0) return { coded: 0, total: 0, percentage: 0 };

    const codedCount = chunks.filter(
      (chunk) => chunk.behavioral_codes && chunk.behavioral_codes.length > 0
    ).length;

    return {
      coded: codedCount,
      total: chunks.length,
      percentage: Math.round((codedCount / chunks.length) * 100),
    };
  }, [chunks]);

  // ========================================
  // Navigation Actions
  // ========================================
  const nextChunk = useCallback(() => {
    setSelectedChunkIndex((prev) => Math.min(prev + 1, chunks.length - 1));
  }, [chunks.length]);

  const prevChunk = useCallback(() => {
    setSelectedChunkIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const nextDocument = useCallback(() => {
    setCurrentDocIndex((prev) => Math.min(prev + 1, documents.length - 1));
  }, [documents.length]);

  const prevDocument = useCallback(() => {
    setCurrentDocIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const selectChunk = useCallback((index) => {
    setSelectedChunkIndex(index);
  }, []);

  const selectDocument = useCallback((index) => {
    setCurrentDocIndex(index);
  }, []);

  // ========================================
  // Category Toggle
  // ========================================
  const toggleCategory = useCallback((category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  }, []);

  // ========================================
  // Code Application Actions
  // ========================================
  const applyCode = useCallback(
    async (codeName, options = {}) => {
      if (!currentChunk?.id) return;

      try {
        const updatedCodes = await codeService.applyCodeToChunk(currentChunk.id, {
          entityId,
          code: codeName,
          confidence: options.confidence || 0.9,
          notes: options.notes || '',
          taxonomy: options.taxonomy || 'faana_core',
        });

        // Update chunk in local state
        setChunks((prev) =>
          prev.map((chunk, i) =>
            i === selectedChunkIndex
              ? { ...chunk, behavioral_codes: updatedCodes }
              : chunk
          )
        );

        // Remove newly applied code from AI suggestions (if present)
        setAiSuggestions((prev) => {
          const updated = prev.filter((s) => s.code !== codeName);
          if (updated.length !== prev.length && currentChunk?.id) {
            aiCacheRef.current.set(currentChunk.id, { suggestions: updated, hasFetched: true });
          }
          return updated;
        });

        // Optionally move to next chunk after applying
        if (options.advanceToNext) {
          nextChunk();
        }

        return updatedCodes;
      } catch (error) {
        console.error('Failed to apply code:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentChunk?.id, selectedChunkIndex, nextChunk]
  );

  const removeCode = useCallback(
    async (chunkIdOrCodeName, codeName) => {
      // Support both signatures:
      // removeCode(chunkId, codeName) - explicit chunk ID (from DocumentViewer)
      // removeCode(codeName) - uses current chunk (legacy / from CodingPanel)
      let targetChunkId;
      let targetCodeName;

      if (codeName === undefined) {
        // Legacy: removeCode(codeName) - use current chunk
        targetChunkId = currentChunk?.id;
        targetCodeName = chunkIdOrCodeName;
      } else {
        // New: removeCode(chunkId, codeName)
        targetChunkId = chunkIdOrCodeName;
        targetCodeName = codeName;
      }

      if (!targetChunkId) return;

      try {
        const updatedCodes = await codeService.removeCodeFromChunk(
          targetChunkId,
          targetCodeName,
          entityId
        );

        // Update chunk in local state by matching ID
        setChunks((prev) =>
          prev.map((chunk) =>
            (chunk.id || chunk.chunkId) === targetChunkId
              ? { ...chunk, behavioral_codes: updatedCodes }
              : chunk
          )
        );

        return updatedCodes;
      } catch (error) {
        console.error('Failed to remove code:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentChunk?.id]
  );

  const updateCode = useCallback(
    async (codeName, updates) => {
      if (!currentChunk?.id) return;

      try {
        const updatedCodes = await codeService.updateChunkCode(
          currentChunk.id,
          codeName,
          { ...updates, entityId }
        );

        // Update chunk in local state
        setChunks((prev) =>
          prev.map((chunk, i) =>
            i === selectedChunkIndex
              ? { ...chunk, behavioral_codes: updatedCodes }
              : chunk
          )
        );

        return updatedCodes;
      } catch (error) {
        console.error('Failed to update code:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentChunk?.id, selectedChunkIndex]
  );

  // ========================================
  // Refresh Functions
  // ========================================
  const refreshCodes = useCallback(async () => {
    try {
      setCodesLoading(true);
      const loadedCodes = await codeService.listCodes();
      setCodes(loadedCodes);
    } finally {
      setCodesLoading(false);
    }
  }, []);

  const refreshDocuments = useCallback(async () => {
    if (!entityId) return;
    try {
      setDocumentsLoading(true);
      const loadedDocs = await codeService.getEntityDocuments(entityId);
      setDocuments(loadedDocs);
    } finally {
      setDocumentsLoading(false);
    }
  }, [entityId]);

  // ========================================
  // AI Suggestion Functions
  // ========================================

  /**
   * Fetch AI suggestions for the current chunk
   */
  const fetchAISuggestions = useCallback(
    async (options = {}) => {
      if (!currentChunk?.id || !entityId) {
        setAiSuggestions([]);
        return;
      }

      // Skip if already cached for this chunk (unless forced)
      if (!options.force && aiCacheRef.current.has(currentChunk.id)) {
        return;
      }

      try {
        setAiLoading(true);
        setAiError(null);

        const result = await codeService.getAISuggestions(
          currentChunk.id,
          entityId,
          {
            includeExplanation: true,
            minConfidence: options.minConfidence || 0.5,
          }
        );

        if (result.success) {
          const rawSuggestions = result.suggestions || [];
          // Filter out codes already applied to this chunk
          const appliedCodeNames = new Set(
            (currentChunk.behavioral_codes || []).map((c) => c.code)
          );
          const suggestions = rawSuggestions.filter((s) => !appliedCodeNames.has(s.code));
          setAiSuggestions(suggestions);
          setAiHasFetched(true);
          // Cache the result
          aiCacheRef.current.set(currentChunk.id, { suggestions, hasFetched: true });
        } else {
          setAiError(result.overallAnalysis || 'Failed to generate suggestions');
          setAiSuggestions([]);
          // Cache the empty result so we don't re-fetch failures
          aiCacheRef.current.set(currentChunk.id, { suggestions: [], hasFetched: true });
        }
      } catch (error) {
        console.error('Failed to fetch AI suggestions:', error);
        setAiError(error.message);
        setAiSuggestions([]);
      } finally {
        setAiLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentChunk?.id, entityId]
  );

  /**
   * Accept an AI suggestion - apply the code to current chunk
   */
  const acceptSuggestion = useCallback(
    async (suggestion) => {
      if (!currentChunk?.id) return;

      try {
        setProcessingAiAction(suggestion.code);

        await applyCode(suggestion.code, {
          confidence: suggestion.confidence,
          notes: suggestion.reasoning ? `AI: ${suggestion.reasoning.substring(0, 200)}` : '',
          advanceToNext: false,
        });

        // Remove suggestion from list and update cache
        setAiSuggestions((prev) => {
          const updated = prev.filter((s) => s.code !== suggestion.code);
          if (currentChunk?.id) {
            aiCacheRef.current.set(currentChunk.id, { suggestions: updated, hasFetched: true });
          }
          return updated;
        });
      } catch (error) {
        console.error('Failed to accept suggestion:', error);
        throw error;
      } finally {
        setProcessingAiAction(null);
      }
    },
    [currentChunk?.id, applyCode]
  );

  /**
   * Reject an AI suggestion - just remove it from the list
   */
  const rejectSuggestion = useCallback((suggestion) => {
    setAiSuggestions((prev) => {
      const updated = prev.filter((s) => s.code !== suggestion.code);
      if (currentChunk?.id) {
        aiCacheRef.current.set(currentChunk.id, { suggestions: updated, hasFetched: true });
      }
      return updated;
    });
  }, [currentChunk?.id]);

  /**
   * Accept all AI suggestions
   * Captures aiSuggestions at call time to avoid stale closure issues
   */
  const acceptAllSuggestions = useCallback(async () => {
    if (!currentChunk?.id) return;

    // Capture the current suggestions to avoid stale closure
    // setAiSuggestions gives us access to current state
    let suggestionsToProcess = [];
    setAiSuggestions((current) => {
      suggestionsToProcess = [...current];
      return current; // Don't modify yet, just capture
    });

    if (suggestionsToProcess.length === 0) return;

    // Process one at a time to maintain order
    for (const suggestion of suggestionsToProcess) {
      try {
        setProcessingAiAction(suggestion.code);
        await applyCode(suggestion.code, {
          confidence: suggestion.confidence,
          notes: suggestion.reasoning ? `AI: ${suggestion.reasoning.substring(0, 200)}` : '',
          advanceToNext: false,
        });
      } catch (error) {
        console.error(`Failed to accept suggestion ${suggestion.code}:`, error);
      }
    }

    setAiSuggestions([]);
    if (currentChunk?.id) {
      aiCacheRef.current.set(currentChunk.id, { suggestions: [], hasFetched: true });
    }
    setProcessingAiAction(null);
  }, [currentChunk?.id, applyCode]);

  /**
   * Clear AI suggestions (when changing chunks)
   */
  const clearAISuggestions = useCallback(() => {
    setAiSuggestions([]);
    setAiError(null);
    setAiHasFetched(false);
    aiCacheRef.current.clear();
  }, []);

  // Persist aiAutoFetch preference
  useEffect(() => {
    localStorage.setItem('behavioralWorkbench_aiAutoFetch', String(aiAutoFetch));
  }, [aiAutoFetch]);

  // Restore cached suggestions on chunk change, or clear for uncached chunks
  useEffect(() => {
    if (!currentChunk?.id) {
      setAiSuggestions([]);
      setAiHasFetched(false);
      setAiError(null);
      return;
    }

    const cached = aiCacheRef.current.get(currentChunk.id);
    if (cached) {
      // Filter out any suggestions for codes already applied to this chunk
      const appliedCodeNames = new Set(
        (currentChunk.behavioral_codes || []).map((c) => c.code)
      );
      const filtered = cached.suggestions.filter((s) => !appliedCodeNames.has(s.code));
      setAiSuggestions(filtered);
      setAiHasFetched(cached.hasFetched);
      setAiError(null);
    } else {
      setAiSuggestions([]);
      setAiHasFetched(false);
      setAiError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChunk?.id]);

  // Auto-fetch AI suggestions for uncached chunks (debounced)
  // 1500ms delay avoids Vertex AI 429 rate limits when navigating quickly
  useEffect(() => {
    if (!aiAutoFetch || !currentChunk?.id || !entityId) return;
    if (!currentChunk.content && !currentChunk.text) return;
    if (aiCacheRef.current.has(currentChunk.id)) return;

    const timer = setTimeout(() => {
      fetchAISuggestions();
    }, 1500);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChunk?.id, aiAutoFetch, entityId, fetchAISuggestions]);

  // ========================================
  // Return State and Actions
  // ========================================
  return {
    // Code registry
    codes,
    codesLoading,
    codesError,
    codesByDimension,
    filteredCodes,
    filteredCodesByDimension,

    // Documents
    documents,
    currentDocument,
    currentDocIndex,
    documentsLoading,
    documentsError,

    // Chunks
    chunks,
    currentChunk,
    selectedChunkIndex,
    chunksLoading,

    // Progress
    documentProgress,

    // UI state
    searchTerm,
    setSearchTerm,
    expandedCategories,
    toggleCategory,
    learningMode,
    setLearningMode,

    // Navigation
    nextChunk,
    prevChunk,
    nextDocument,
    prevDocument,
    selectChunk,
    selectDocument,

    // Code actions
    applyCode,
    removeCode,
    updateCode,

    // AI suggestions
    aiSuggestions,
    aiLoading,
    aiError,
    processingAiAction,
    aiAutoFetch,
    setAiAutoFetch,
    aiHasFetched,
    fetchAISuggestions,
    acceptSuggestion,
    rejectSuggestion,
    acceptAllSuggestions,
    clearAISuggestions,

    // Refresh
    refreshCodes,
    refreshDocuments,
  };
}

export default useWorkbenchState;
